import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { FeedbackRating, FeedbackSummary } from "../api/types";

interface Props {
  postId: string;
  feedback: FeedbackSummary;
  /** Compact (for cards) uses icon-only buttons; full shows comment field inline. */
  variant?: "compact" | "full";
}

/**
 * Thumbs up / down with an optional comment. On the dashboard card (compact),
 * a click opens a small popover with a comment field; on PostDetail (full),
 * the comment field is inline.
 */
export function FeedbackButtons({ postId, feedback, variant = "compact" }: Props) {
  const qc = useQueryClient();
  const [pendingRating, setPendingRating] = useState<FeedbackRating | null>(null);
  const [comment, setComment] = useState("");

  const rate = useMutation({
    mutationFn: (body: { rating: FeedbackRating; comment?: string }) =>
      api.posts.rate(postId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["post", postId] });
      void qc.invalidateQueries({ queryKey: ["posts"] });
      void qc.invalidateQueries({ queryKey: ["post-feedback", postId] });
      setPendingRating(null);
      setComment("");
    },
  });

  const latest = feedback.latest?.rating;
  const thumbsUpActive = latest === "up";
  const thumbsDownActive = latest === "down";

  const pick = (rating: FeedbackRating, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (variant === "compact") {
      setPendingRating(rating);
    } else {
      setPendingRating(rating);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!pendingRating) return;
    rate.mutate({
      rating: pendingRating,
      comment: comment.trim() || undefined,
    });
  };

  const cancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPendingRating(null);
    setComment("");
  };

  const quickSend = (rating: FeedbackRating, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    rate.mutate({ rating });
  };

  return (
    <div className="relative inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={(e) => (variant === "compact" ? quickSend("up", e) : pick("up", e))}
        onContextMenu={(e) => pick("up", e)}
        disabled={rate.isPending}
        title={variant === "compact" ? "Thumbs up (right-click to add comment)" : "Thumbs up"}
        className={`group flex h-8 w-8 items-center justify-center rounded-full transition ${
          thumbsUpActive
            ? "bg-emerald-500 text-white"
            : "bg-white/80 text-brand-text-dark/60 hover:bg-emerald-100 hover:text-emerald-700"
        }`}
      >
        <ThumbUp />
      </button>
      <button
        type="button"
        onClick={(e) => (variant === "compact" ? quickSend("down", e) : pick("down", e))}
        onContextMenu={(e) => pick("down", e)}
        disabled={rate.isPending}
        title={variant === "compact" ? "Thumbs down (right-click to add comment)" : "Thumbs down"}
        className={`group flex h-8 w-8 items-center justify-center rounded-full transition ${
          thumbsDownActive
            ? "bg-red-500 text-white"
            : "bg-white/80 text-brand-text-dark/60 hover:bg-red-100 hover:text-red-700"
        }`}
      >
        <ThumbDown />
      </button>
      {(feedback.up_count > 0 || feedback.down_count > 0) && variant === "compact" ? (
        <span className="text-[10px] text-brand-text-dark/50 tabular-nums">
          {feedback.up_count > 0 ? `↑${feedback.up_count}` : ""}
          {feedback.up_count > 0 && feedback.down_count > 0 ? " " : ""}
          {feedback.down_count > 0 ? `↓${feedback.down_count}` : ""}
        </span>
      ) : null}

      {pendingRating ? (
        <form
          onSubmit={submit}
          onClick={(e) => e.stopPropagation()}
          className={
            variant === "compact"
              ? "absolute bottom-10 left-0 z-50 w-72 rounded-xl bg-white p-3 shadow-xl ring-1 ring-black/10"
              : "mt-3 w-full rounded-xl bg-white p-3 shadow-sm ring-1 ring-black/10"
          }
        >
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-brand-text-dark/60">
            {pendingRating === "up" ? "Was war gut?" : "Was war nicht gut?"}
          </div>
          <textarea
            autoFocus
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Kommentar (optional)"
            rows={3}
            className="w-full resize-y rounded-lg border border-black/10 bg-brand-beige px-3 py-2 text-sm focus:border-brand-blue focus:outline-none"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={cancel}
              className="rounded-full px-3 py-1.5 text-xs text-brand-text-dark/60 hover:bg-black/5"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={rate.isPending}
              className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white ${
                pendingRating === "up"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-red-600 hover:bg-red-700"
              } disabled:opacity-50`}
            >
              {rate.isPending ? "…" : "Senden"}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

function ThumbUp() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M2 9.5A1.5 1.5 0 013.5 8H5v8H3.5A1.5 1.5 0 012 14.5v-5zM6.5 8h7.85a2 2 0 012 2.3l-.75 4.5A2 2 0 0113.6 16.5H6.5V8zm2-1V4.5a1.5 1.5 0 113 0v2l-1.2 1H8.5V7z" />
    </svg>
  );
}
function ThumbDown() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M2 5.5A1.5 1.5 0 013.5 4H5v8H3.5A1.5 1.5 0 012 10.5v-5zM6.5 4h7.1a2 2 0 011.98 1.7l.75 4.5a2 2 0 01-1.98 2.3H6.5V4zm2 9.5l1.3 1h1.3a1.5 1.5 0 11-3 0V13.5h.4z" />
    </svg>
  );
}
